import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  MessageCircle, Send, ArrowLeft, HelpCircle, 
  BookOpen, Clock, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Support = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'general'
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user) {
      fetchTickets();
    }
  }, [user, authLoading, navigate]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, { withCredentials: true });
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support/ticket`, formData, { withCredentials: true });
      toast.success('Support ticket submitted!');
      setFormData({ subject: '', message: '', category: 'general' });
      setShowForm(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const backPath = user?.role === 'parent' ? '/parent/dashboard' : '/student/dashboard';

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to={backPath} className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">Support Center</h1>
            <p className="text-[#94A3B8]">Get help with your questions</p>
          </div>
        </div>

        {/* Quick Help */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <HelpCircle className="w-10 h-10 text-[#00AEEF] mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">FAQs</h3>
            <p className="text-[#94A3B8] text-sm">Find answers to common questions</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <BookOpen className="w-10 h-10 text-[#00FFE0] mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Guides</h3>
            <p className="text-[#94A3B8] text-sm">Step-by-step tutorials</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
            <MessageCircle className="w-10 h-10 text-[#FF6A00] mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Contact</h3>
            <p className="text-[#94A3B8] text-sm">info@nextgentechpath.org</p>
          </div>
        </div>

        {/* Submit Ticket Button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all flex items-center justify-center gap-2 mb-8"
          >
            <MessageCircle className="w-5 h-5" />
            Submit a Support Ticket
          </button>
        )}

        {/* Ticket Form */}
        {showForm && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8 mb-8">
            <h2 className="text-xl font-bold text-white mb-6">New Support Ticket</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="bg-[#060F22] border border-[#00AEEF]/30 text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all"
                >
                  <option value="general">General Question</option>
                  <option value="technical">Technical Issue</option>
                  <option value="course">Course Content</option>
                  <option value="account">Account Issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full min-h-[150px] transition-all resize-none"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-[#060F22] text-[#94A3B8] font-bold rounded-xl px-6 py-3 border border-[#00AEEF]/20 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tickets List */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Your Tickets</h2>
          {tickets.length > 0 ? (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-white">{ticket.subject}</h3>
                      <p className="text-[#94A3B8] text-sm">{ticket.category}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      ticket.status === 'open' ? 'bg-[#00AEEF]/20 text-[#00AEEF]' :
                      ticket.status === 'resolved' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-3">{ticket.message}</p>
                  <p className="text-[#94A3B8] text-xs flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8 text-center">
              <MessageCircle className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#94A3B8]">No support tickets yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
